import type { EmailDoDeca } from '../../servicos/modelosDeEmail';

/** Os e-mails do fluxo mensal do Recorrente, na ordem em que saem. */
const ORDEM_DO_FLUXO = [
  'DESCRITIVO_OPERACIONAL',
  'FORNECEDOR_TRANSFERENCIA_CREDITO',
  'CLIENTE_CERTIFICADO_TRANSFERENCIA',
  'CLIENTE_CERTIFICADO_APROPRIACAO',
  'CLIENTE_AJUSTE_CREDITO',
  'CLIENTE_PAGAMENTO_CREDITO',
  'ASSESSOR_HONORARIOS_CREDITO',
] as const;

export interface LinhaDoGrupo {
  readonly email: EmailDoDeca;
  /** A posição no fluxo mensal — só nele. */
  readonly numero: number | null;
  /** O que a linha diz sob o nome: no fluxo, o dia; nos outros, quando e para quem. */
  readonly quando: string;
}

export interface GrupoDeEmails {
  readonly titulo: string;
  readonly nota: string | null;
  readonly linhas: readonly LinhaDoGrupo[];
}

/**
 * Os e-mails em grupos, como as duas abas mostram (E-mails e Automações › De
 * e-mail). O fluxo mensal vem numerado, na ordem em que os e-mails saem, com o
 * dia de cada um — é ele que conta a história do mês, sem um quadro à parte.
 */
export function agruparEmails(emails: readonly EmailDoDeca[]): GrupoDeEmails[] {
  const grupos = new Map<string, EmailDoDeca[]>();
  for (const e of emails) grupos.set(e.grupo, [...(grupos.get(e.grupo) ?? []), e]);

  return [...grupos.entries()].map(([titulo, doGrupo]) => {
    const doFluxo = doGrupo.some((e) => e.cronograma);
    if (!doFluxo) {
      return {
        titulo,
        nota: null,
        linhas: doGrupo.map((email) => ({ email, numero: null, quando: email.quando })),
      };
    }

    const posicao = (e: EmailDoDeca): number => {
      const i = ORDEM_DO_FLUXO.indexOf(e.id as (typeof ORDEM_DO_FLUXO)[number]);
      return i === -1 ? ORDEM_DO_FLUXO.length : i;
    };
    const ordenados = [...doGrupo].sort((a, b) => posicao(a) - posicao(b));
    const transferencia = doGrupo.find((e) => e.id === 'FORNECEDOR_TRANSFERENCIA_CREDITO');
    const quando = transferencia?.automacao?.data?.quando;
    const piso = quando !== undefined && 'pisoPercentual' in quando ? quando.pisoPercentual : 10;

    return {
      titulo,
      nota: `Na ordem em que saem, todo mês do contrato. Só entram os meses com limite de utilização acima de ${piso}%.`,
      linhas: ordenados.map((email) => ({
        email,
        numero: posicao(email) < ORDEM_DO_FLUXO.length ? posicao(email) + 1 : null,
        quando:
          email.automacao?.data?.descricao ??
          primeiraLetraMinuscula(email.automacao?.gatilho) ??
          email.quando,
      })),
    };
  });
}

/** "O fornecedor anexa…" vira "Quando o fornecedor anexa…" — a linha lê como o momento. */
function primeiraLetraMinuscula(texto: string | undefined): string | undefined {
  if (texto === undefined) return undefined;
  return `Quando ${texto.charAt(0).toLowerCase()}${texto.slice(1)}`;
}
